/**
 * Translation system for Slovak payroll functionality
 * Default language is English with Slovak translations available
 */

export type PayrollLanguage = 'en' | 'sk';

export const translations = {
  en: {
    // Main titles
    payrollSystem: 'Slovak Payroll System',
    payrollCalculation: 'Payroll Calculation',
    payslip: 'Payslip',
    paymentOrders: 'Payment Orders',
    
    // Navigation
    calculate: 'Calculate',
    employees: 'Employees',
    payslips: 'Payslips',
    payments: 'Payments',
    
    // Employee form
    personalInfo: 'Personal Information',
    employmentDetails: 'Employment Details',
    insurance: 'Insurance',
    taxSettings: 'Tax Settings',
    bankDetails: 'Bank Details',
    additionalDeductions: 'Additional Deductions',
    children: 'Children',
    
    // Fields
    fullName: 'Full Name',
    email: 'Email',
    birthNumber: 'Birth Number',
    idCardNumber: 'ID Card Number',
    permanentAddress: 'Permanent Address',
    contractType: 'Contract Type',
    weeklyHours: 'Weekly Hours',
    startDate: 'Start Date',
    endDate: 'End Date',
    healthInsuranceCompany: 'Health Insurance Company',
    isPensioner: 'Pensioner',
    isStudent: 'Student',
    isDisabled: 'Disabled Person',
    hasSevereDisability: 'Severe Disability (2% Health Insurance)',
    appliesTaxFreeAllowance: 'Applies Tax-Free Allowance',
    appliesChildTaxBonus: 'Applies Child Tax Bonus',
    appliesSpouseAllowance: 'Applies Spouse Allowance',
    iban: 'IBAN',
    bankCode: 'Bank Code',
    thirdPillarContribution: '3rd Pillar Contribution',
    unionContribution: 'Union Contribution',
    hasGarnishments: 'Has Garnishments',
    
    // Contract types
    contractTypes: {
      TPP: 'Permanent Employment',
      HALF_TIME: 'Part-time Employment',
      DoVP: 'Work Agreement',
      DoPČ: 'Work Activity Agreement',
      DoBPŠ: 'Student Work Agreement'
    },
    
    // Payroll components
    grossSalary: 'Gross Salary',
    baseSalary: 'Base Salary',
    overtime: 'Overtime',
    nightWork: 'Night Work',
    weekendWork: 'Weekend Work',
    holidayWork: 'Holiday Work',
    sickLeave: 'Sick Leave',
    vacation: 'Vacation',
    bonuses: 'Bonuses',
    
    // Insurance
    socialInsurance: 'Social Insurance',
    healthInsurance: 'Health Insurance',
    pensionInsurance: 'Pension Insurance',
    disabilityInsurance: 'Disability Insurance',
    sicknessInsurance: 'Sickness Insurance',
    unemploymentInsurance: 'Unemployment Insurance',
    accidentInsurance: 'Accident Insurance',
    guaranteeInsurance: 'Guarantee Insurance',
    solidarityFund: 'Solidarity Fund',
    
    // Tax
    incomeTax: 'Income Tax',
    taxBase: 'Tax Base',
    taxFreeAllowance: 'Tax-Free Allowance',
    taxableIncome: 'Taxable Income',
    taxPrepayment: 'Tax Prepayment',
    childTaxBonus: 'Child Tax Bonus',
    finalTax: 'Final Tax',
    
    // Deductions
    mealVouchers: 'Meal Vouchers',
    garnishments: 'Garnishments',
    advancePayment: 'Advance Payment',
    otherDeductions: 'Other Deductions',
    
    // Final amounts
    netSalary: 'Net Salary',
    finalPayout: 'Amount to Pay',
    employerCost: 'Total Employer Cost',
    assessmentBase: 'Assessment Base',
    
    // Actions (Note: Manual calculations removed - these are for legacy compatibility)
    calculatePayroll: 'View Payroll Data',
    batchCalculate: 'View All Payroll',
    exportCSV: 'Export CSV',
    downloadPDF: 'Download PDF',
    print: 'Print',
    addEmployee: 'Add Employee',
    updateEmployee: 'Update Employee',
    addChild: 'Add Child',
    save: 'Save',
    cancel: 'Cancel',
    
    // Messages
    loadingData: 'Loading payroll data...',
    calculatingPayroll: 'Loading payroll data...',
    savingEmployee: 'Saving...',
    selectEmployee: 'Select Employee',
    selectPeriod: 'Select Period',
    noDataAvailable: 'No data available',
    calculationComplete: 'Calculation Complete',
    errorOccurred: 'An error occurred',
    requiredField: 'Required field',
    invalidFormat: 'Invalid format',
    
    // Monthly data
    workedHours: 'Worked Hours',
    regularHours: 'Regular Hours',
    overtimeHours: 'Overtime Hours',
    nightHours: 'Night Hours',
    weekendHours: 'Weekend Hours',
    holidayHours: 'Holiday Hours',
    vacationDays: 'Vacation Days',
    sickLeaveDays: 'Sick Leave Days',
    paidLeaveHours: 'Paid Leave Hours',
    unpaidLeaveHours: 'Unpaid Leave Hours',
    standardWorkingDays: 'Standard Working Days',
    standardWorkingHours: 'Standard Working Hours',
    
    // Payment institutions
    socialInsuranceOffice: 'Social Insurance Office',
    healthInsuranceCompanies: {
      'VšZP': 'General Health Insurance',
      'Dôvera': 'Dôvera Health Insurance',
      'Union': 'Union Health Insurance'
    },
    taxAuthority: 'Tax Authority',
    
    // Payment details
    variableSymbol: 'Variable Symbol',
    specificSymbol: 'Specific Symbol',
    dueDate: 'Due Date',
    employeePayments: 'Employee Payments',
    institutionalPayments: 'Institutional Payments',
    paymentSummary: 'Payment Summary',
    
    // Period
    period: 'Period',
    month: 'Month',
    year: 'Year',
    months: ['January', 'February', 'March', 'April', 'May', 'June', 
             'July', 'August', 'September', 'October', 'November', 'December']
  },
  
  sk: {
    // Main titles
    payrollSystem: 'Slovenský mzdový systém',
    payrollCalculation: 'Výpočet mzdy',
    payslip: 'Výplatná páska',
    paymentOrders: 'Platobné príkazy',
    
    // Navigation
    calculate: 'Výpočet',
    employees: 'Zamestnanci',
    payslips: 'Výplatné pásky',
    payments: 'Platby',
    
    // Employee form
    personalInfo: 'Osobné údaje',
    employmentDetails: 'Pracovný pomer',
    insurance: 'Poistenie',
    taxSettings: 'Daňové nastavenia',
    bankDetails: 'Bankové údaje',
    additionalDeductions: 'Dodatočné zrážky',
    children: 'Deti',
    
    // Fields
    fullName: 'Meno a priezvisko',
    email: 'Email',
    birthNumber: 'Rodné číslo',
    idCardNumber: 'Číslo OP',
    permanentAddress: 'Trvalá adresa',
    contractType: 'Typ zmluvy',
    weeklyHours: 'Týždenný úväzok',
    startDate: 'Dátum nástupu',
    endDate: 'Dátum ukončenia',
    healthInsuranceCompany: 'Zdravotná poisťovňa',
    isPensioner: 'Dôchodca',
    isStudent: 'Študent',
    isDisabled: 'ZŤP',
    hasSevereDisability: 'Ťažké ZŤP (2% ZP)',
    appliesTaxFreeAllowance: 'Uplatňuje nezdaniteľnú časť základu dane',
    appliesChildTaxBonus: 'Uplatňuje daňový bonus na deti',
    appliesSpouseAllowance: 'Uplatňuje nezdaniteľnú časť na manžela/manželku',
    iban: 'IBAN',
    bankCode: 'Kód banky',
    thirdPillarContribution: 'III. pilier',
    unionContribution: 'Odborové príspevky',
    hasGarnishments: 'Má exekúcie',
    
    // Contract types
    contractTypes: {
      TPP: 'Trvalý pracovný pomer',
      HALF_TIME: 'Polovičný úväzok',
      DoVP: 'Dohoda o vykonaní práce',
      DoPČ: 'Dohoda o pracovnej činnosti',
      DoBPŠ: 'Brigáda študentov'
    },
    
    // Payroll components
    grossSalary: 'Hrubá mzda',
    baseSalary: 'Základná mzda',
    overtime: 'Nadčasy',
    nightWork: 'Nočná práca',
    weekendWork: 'Víkendová práca',
    holidayWork: 'Práca vo sviatok',
    sickLeave: 'PN',
    vacation: 'Dovolenka',
    bonuses: 'Odmeny',
    
    // Insurance
    socialInsurance: 'Sociálne poistenie',
    healthInsurance: 'Zdravotné poistenie',
    pensionInsurance: 'Starobné poistenie',
    disabilityInsurance: 'Invalidné poistenie',
    sicknessInsurance: 'Nemocenské poistenie',
    unemploymentInsurance: 'Poistenie v nezamestnanosti',
    accidentInsurance: 'Úrazové poistenie',
    guaranteeInsurance: 'Garančné poistenie',
    solidarityFund: 'Rezervný fond solidarity',
    
    // Tax
    incomeTax: 'Daň z príjmu',
    taxBase: 'Základ dane',
    taxFreeAllowance: 'Nezdaniteľná časť',
    taxableIncome: 'Zdaniteľný príjem',
    taxPrepayment: 'Preddavok na daň',
    childTaxBonus: 'Daňový bonus na deti',
    finalTax: 'Výsledná daň',
    
    // Deductions
    mealVouchers: 'Stravné lístky',
    garnishments: 'Exekúcie',
    advancePayment: 'Záloha',
    otherDeductions: 'Ostatné zrážky',
    
    // Final amounts
    netSalary: 'Čistá mzda',
    finalPayout: 'K výplate',
    employerCost: 'Celková cena práce',
    assessmentBase: 'Vymeriavací základ',
    
    // Actions (Poznámka: Manuálne výpočty odstránené - toto je pre kompatibilitu)
    calculatePayroll: 'Zobraziť údaje o mzde',
    batchCalculate: 'Zobraziť všetky mzdy',
    exportCSV: 'Exportovať CSV',
    downloadPDF: 'Stiahnuť PDF',
    print: 'Tlačiť',
    addEmployee: 'Pridať zamestnanca',
    updateEmployee: 'Aktualizovať zamestnanca',
    addChild: 'Pridať dieťa',
    save: 'Uložiť',
    cancel: 'Zrušiť',
    
    // Messages
    loadingData: 'Načítavam údaje...',
    calculatingPayroll: 'Načítavam údaje o mzde...',
    savingEmployee: 'Ukladám...',
    selectEmployee: 'Vyberte zamestnanca',
    selectPeriod: 'Vyberte obdobie',
    noDataAvailable: 'Žiadne údaje',
    calculationComplete: 'Výpočet dokončený',
    errorOccurred: 'Nastala chyba',
    requiredField: 'Povinné pole',
    invalidFormat: 'Neplatný formát',
    
    // Monthly data
    workedHours: 'Odpracované hodiny',
    regularHours: 'Riadne hodiny',
    overtimeHours: 'Nadčasy',
    nightHours: 'Nočné hodiny',
    weekendHours: 'Víkendové hodiny',
    holidayHours: 'Sviatky',
    vacationDays: 'Dni dovolenky',
    sickLeaveDays: 'Dni PN',
    paidLeaveHours: 'Platené voľno',
    unpaidLeaveHours: 'Neplatené voľno',
    standardWorkingDays: 'Štandardné pracovné dni',
    standardWorkingHours: 'Štandardné pracovné hodiny',
    
    // Payment institutions
    socialInsuranceOffice: 'Sociálna poisťovňa',
    healthInsuranceCompanies: {
      'VšZP': 'Všeobecná zdravotná poisťovňa',
      'Dôvera': 'Dôvera zdravotná poisťovňa',
      'Union': 'Union zdravotná poisťovňa'
    },
    taxAuthority: 'Daňový úrad',
    
    // Payment details
    variableSymbol: 'Variabilný symbol',
    specificSymbol: 'Špecifický symbol',
    dueDate: 'Splatnosť',
    employeePayments: 'Platby zamestnancom',
    institutionalPayments: 'Platby inštitúciám',
    paymentSummary: 'Súhrn platieb',
    
    // Period
    period: 'Obdobie',
    month: 'Mesiac',
    year: 'Rok',
    months: ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún',
             'Júl', 'August', 'September', 'Október', 'November', 'December']
  }
};

export class PayrollTranslator {
  private language: PayrollLanguage = 'en';
  
  constructor(language?: PayrollLanguage) {
    if (language) {
      this.language = language;
    }
  }
  
  setLanguage(language: PayrollLanguage) {
    this.language = language;
  }
  
  getLanguage(): PayrollLanguage {
    return this.language;
  }
  
  t(key: string): any {
    const keys = key.split('.');
    let value: any = translations[this.language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if translation not found
        value = translations.en;
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k];
          } else {
            return key; // Return key if translation not found
          }
        }
        return value;
      }
    }
    
    // Return the value as-is (could be string, array, object, etc.)
    return value !== undefined && value !== null ? value : key;
  }
  
  // Helper method for contract types
  getContractTypeName(type: string): string {
    const contractTypes = translations[this.language].contractTypes;
    return contractTypes[type as keyof typeof contractTypes] || type;
  }
  
  // Helper method for health insurance companies
  getHealthInsuranceName(company: string): string {
    const companies = translations[this.language].healthInsuranceCompanies;
    return companies[company as keyof typeof companies] || company;
  }
  
  // Helper method for months
  getMonthName(monthIndex: number): string {
    return translations[this.language].months[monthIndex] || '';
  }
}

// Create a singleton instance
export const payrollTranslator = new PayrollTranslator();

// React hook for using translations
import { useState, useCallback } from 'react';

export const usePayrollTranslation = (initialLanguage: PayrollLanguage = 'en') => {
  const [language, setLanguage] = useState<PayrollLanguage>(initialLanguage);
  const [translator] = useState(() => new PayrollTranslator(initialLanguage));
  
  const changeLanguage = useCallback((newLanguage: PayrollLanguage) => {
    setLanguage(newLanguage);
    translator.setLanguage(newLanguage);
  }, [translator]);
  
  const t = useCallback((key: string) => {
    return translator.t(key);
  }, [translator, language]); // Include language to trigger re-render
  
  return {
    t,
    language,
    changeLanguage,
    translator
  };
};